import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './AppDownload.css';

// Schermafbeeldingen van de app (gewone foto's, niet interactief). Vervang of voeg toe door
// bestanden in public/app-screens/ te zetten (verhouding 390:844 zoals een moderne telefoon, bv. 780 x 1688 px) en hier de lijst aan te passen.
// Per taal een eigen set (public/app-screens/{nl,en,fr}/app-N.webp), genomen uit de
// meertalige demo; onbekende taal valt terug op Nederlands.
// Verhoog bij nieuwe afbeeldingen, anders blijft de browser/server de oude uit de cache tonen.
const ASSET_VERSION = 3;
const SCREEN_COUNT = 5;
const SCREEN_LANGS = ['nl', 'en', 'fr'];
const screensFor = (lang) => {
  const l = String(lang || 'nl').slice(0, 2);
  const dir = SCREEN_LANGS.includes(l) ? l : 'nl';
  return Array.from({ length: SCREEN_COUNT }, (_, i) => i + 1).map((n) => `/app-screens/${dir}/app-${n}.webp?v=${ASSET_VERSION}`);
};


// Vaste onderste tabbalk van de mockup (zoals in de echte app): blijft staan terwijl
// de schermen wisselen; het gekozen icoon (en het lichtblauwe vlakje) schuift mee.
const TAB_W = 65;
const TAB_ICONS = [
  // Kalender
  <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 10H21M8 2V6M16 2V6" /></>,
  // Schoonmaak
  <><path d="M11 3L12.6 8.4L18 10L12.6 11.6L11 17L9.4 11.6L4 10L9.4 8.4L11 3Z" strokeWidth="1.6" /><path d="M18 14L18.7 16.3L21 17L18.7 17.7L18 20L17.3 17.7L15 17L17.3 16.3L18 14Z" strokeWidth="1.3" /></>,
  // Toegangsbeheer
  <><rect x="4.5" y="10.5" width="15" height="10" rx="2.2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /><circle cx="12" cy="15.5" r="1.3" /></>,
  // Automation
  <path d="M4 19V13M10 19V9M16 19V5M22 19H2" />,
  // Meldingen
  <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
  // Profiel
  <><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></>,
];

function PhoneTabBar({ index, onSelect }) {
  return (
    <svg className="phone-tabbar" viewBox="0 0 390 94" aria-hidden="true">
      <rect x="0" y="0" width="390" height="94" fill="#ffffff" />
      <rect x="0" y="0" width="390" height="1" fill="#e3dccf" />
      <rect className="phone-tabbar-pill" x={(TAB_W - 48) / 2} y="10" width="48" height="44" rx="14" fill="#e6f0fb" style={{ transform: `translateX(${index * TAB_W}px)` }} />
      {TAB_ICONS.map((icon, i) => (
        <g key={i} className="phone-tabbar-tab" onClick={() => i < SCREEN_COUNT && onSelect(i)} style={{ cursor: i < SCREEN_COUNT ? 'pointer' : 'default' }}>
          <rect x={i * TAB_W} y="0" width={TAB_W} height="60" fill="transparent" />
          <svg x={i * TAB_W + (TAB_W - 24) / 2} y="20" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: i === index ? '#1565c0' : '#5f6d85', transition: 'stroke 0.25s ease' }}>
            {icon}
          </svg>
        </g>
      ))}
      <circle cx={4 * TAB_W + TAB_W / 2 + 9} cy="18" r="7.5" fill="#e53935" />
      <text x={4 * TAB_W + TAB_W / 2 + 9} y="21.2" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fff" fontFamily="-apple-system, system-ui, sans-serif">3</text>
    </svg>
  );
}

function ScreenCarousel() {
  const { t, i18n } = useTranslation();
  const SCREENS = screensFor(i18n.language);
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  // Tijdens een vloeiende scroll naar een gekozen tab negeren we de tussentijdse
  // scrollposities, anders springt het gekozen icoon eerst terug naar de oude tab.
  const targetRef = useRef(null);
  const targetTimer = useRef(null);

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(SCREENS.length - 1, i));
    targetRef.current = next;
    clearTimeout(targetTimer.current);
    targetTimer.current = setTimeout(() => { targetRef.current = null; }, 900);
    el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
    setIndex(next);
  };

  useEffect(() => () => clearTimeout(targetTimer.current), []);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || !el.clientWidth) return;
    if (targetRef.current !== null) {
      if (Math.abs(el.scrollLeft - targetRef.current * el.clientWidth) < 2) {
        targetRef.current = null;
        clearTimeout(targetTimer.current);
      }
      return;
    }
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setIndex((prev) => (prev === i ? prev : i));
  };

  return (
    <div className="phone-carousel">
      <button type="button" className="phone-arrow" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label={t('calendar.previous')}>&#8249;</button>
      <div className="phone-stack">
        <div className="phone-mockup">
          <div className="phone-notch"></div>
          <div className="phone-screen phone-screen-images">
            <div className="phone-track" ref={trackRef} onScroll={onScroll}>
              {SCREENS.map((src, i) => (
                <img key={src} src={src} alt={`SmartStays app ${i + 1}`} className="phone-slide" loading={i === 0 ? 'eager' : 'lazy'} draggable="false" />
              ))}
            </div>
            <img className="phone-statusbar" src={`/app-screens/statusbar.png?v=${ASSET_VERSION}`} alt="" draggable="false" />
            <PhoneTabBar index={index} onSelect={goTo} />
          </div>
        </div>
        <div className="phone-dots" role="tablist">
          {SCREENS.map((src, i) => (
            <button key={src} type="button" className={`phone-dot ${i === index ? 'active' : ''}`} onClick={() => goTo(i)} aria-label={`${i + 1} / ${SCREENS.length}`} aria-selected={i === index} role="tab" />
          ))}
        </div>
      </div>
      <button type="button" className="phone-arrow" onClick={() => goTo(index + 1)} disabled={index === SCREENS.length - 1} aria-label={t('calendar.next')}>&#8250;</button>
    </div>
  );
}

function AppDownload() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'SmartStays - Download de app';
  }, []);

  return (
    <div className="app-download-container">
      <Navbar />

      <section className="app-download-hero">
        <img src="/app-icon-rounded.png" alt="SmartStays" className="app-download-logo" />
        <h1>{t('appDownload.title')}</h1>

        <div className="app-download-buttons">
          <div className="app-download-btn disabled">
            <span>{t('appDownload.appStore')}</span>
            <span className="app-download-soon">{t('appDownload.comingSoon')}</span>
          </div>
          <div className="app-download-btn disabled">
            <span>{t('appDownload.playStore')}</span>
            <span className="app-download-soon">{t('appDownload.comingSoon')}</span>
          </div>
        </div>
      </section>

      <section className="app-showcase">
        <ScreenCarousel />
      </section>

      <Footer />
    </div>
  );
}

export default AppDownload;
